import { useTranslation } from 'react-i18next'

import { HStack, Text } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { formatJstMonthDayHm, formatJstTimeHm } from '~/models/dateTime'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'
import { marketOpenAt } from '~/pages/IntradayBidding/models/marketOpen'
import {
  minutesBeforeGc,
  toScheduleDayAndTime,
  validateScheduleAppliedAt,
} from '~/pages/IntradayBidding/models/strategySchedule'

type Props = {
  /** 段の適用日時 (絶対時刻)。 時刻が未入力なら `undefined`。 */
  appliedAt: Date | undefined
  /** 受渡日 (= カレンダー日 `Date`)。 */
  deliveryDate: Date
  /** 対象コマ (1〜48)。 */
  koma: number
}

/**
 * 戦略モードの 1 段の適用タイミング表示 (AC-BIN-005b): 前日 / 当日バッジ +
 * 「GC の {N}分前」 (N は GC までの分数。 60 分以上も分のまま。
 * `bidding/bid-limits.md` AC-BDL-502 と表現統一)。
 *
 * 取引窓 (開場 ≤ 適用日時 < GC) の外は相対表現を出さず、 どちら側に外れたかと
 * 基準時刻を示す (= 保存前にその場で直せるように)。
 */
export const IntradayStrategyScheduleTiming = ({
  appliedAt,
  deliveryDate,
  koma,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  if (appliedAt === undefined) {
    return <Text color="Text.lowEmphasis">—</Text>
  }
  const dayAndTime = toScheduleDayAndTime(appliedAt, deliveryDate)
  const violation = validateScheduleAppliedAt({ appliedAt, deliveryDate, koma })
  const gcAt = calcGcAtForKoma(deliveryDate, koma)

  const label = ((): string => {
    if (violation === 'beforeMarketOpen') {
      return t('page:biddingIntraday.strategy.beforeMarketOpen', {
        openAt: formatJstMonthDayHm(marketOpenAt(deliveryDate)),
      })
    }
    if (violation === 'atOrAfterGc') {
      return t('page:biddingIntraday.strategy.atOrAfterGc', {
        gcAt: formatJstTimeHm(gcAt),
      })
    }
    return t('page:biddingIntraday.strategy.gcBeforeMinutes', {
      minutes: minutesBeforeGc(appliedAt, gcAt),
    })
  })()

  return (
    <HStack gap={1}>
      {dayAndTime !== undefined && (
        <Badge status="neutral">
          {t(
            dayAndTime.day === 'dayBefore'
              ? 'page:biddingIntraday.strategy.dayBefore'
              : 'page:biddingIntraday.strategy.deliveryDay',
          )}
        </Badge>
      )}
      <Text
        color={
          violation === undefined ? 'Text.midEmphasis' : 'Text.negativeMid'
        }
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </HStack>
  )
}
